import appLogger from '@/utils/logger';
import { sanitizeEmailHTML } from '@/utils/htmlSanitizer';
import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';
import ShadCnFormTextarea from '../../../components/FormComponents/ShadCnFormTextarea';

type ImproveAnswerModalProps = object;

const ImproveAnswerModal = forwardRef<{ open: () => void; close: () => void }, ImproveAnswerModalProps>((props, ref) => {
  const form = useFormContext();
  const { bot_response, trigger_phrase } = form.getValues();

  appLogger.info('trigger_phrase', { data: trigger_phrase });

  return (
    <div className='flex flex-col gap-6'>
      {/* Header Section */}
      <div className='text-center border-b border-gray-200 pb-4'>
        <h2 className='text-xl font-semibold text-gray-900 mb-1'>Improve AI Response</h2>
      </div>

      {/* Top Row - Read-only content side by side */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* User Message Section */}
        <div className='space-y-3'>
          <div>
            <h3 className='text-base font-semibold text-gray-900 mb-1'>Prospect Message</h3>
            <p className='text-xs text-gray-600 mb-2'>Original question from the prospect</p>
          </div>
          <ShadCnFormTextarea
            name='trigger_phrase'
            placeholder='Enter user message'
            className='w-full bg-gray-50 border-gray-200 text-sm'
            rows={4}
            disabled
          />
        </div>

        {/* Bot Response Section */}
        <div className='space-y-3'>
          <div>
            <h3 className='text-base font-semibold text-gray-900 mb-1'>Current AI Response</h3>
            <p className='text-xs text-gray-600 mb-2'>What the AI currently responded</p>
          </div>
          <div
            className='min-h-[100px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 overflow-y-auto max-h-[120px]'
            aria-disabled
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHTML(bot_response) }}
          ></div>
        </div>
      </div>

      {/* Bottom Row - Input fields side by side */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Expected Response Section */}
        <div className='space-y-3'>
          <div>
            <h3 className='text-base font-semibold text-gray-900 mb-1'>Expected Response</h3>
            <p className='text-xs text-gray-600 mb-2'>Ideal response for AI training</p>
          </div>
          <ShadCnFormTextarea
            name='content_value'
            placeholder='Enter the ideal response...'
            className='w-full border-blue-200 focus:border-blue-400 focus:ring-blue-400 text-sm'
            rows={5}
          />
        </div>

        {/* Feedback Section */}
        <div className='space-y-3'>
          <div>
            <h3 className='text-base font-semibold text-gray-900 mb-1'>Team Feedback</h3>
            <p className='text-xs text-gray-600 mb-2'>Additional feedback for marketbetter team</p>
          </div>
          <ShadCnFormTextarea
            name='feedback'
            placeholder='Share your thoughts...'
            className='w-full border-green-200 focus:border-green-400 focus:ring-green-400 text-sm'
            rows={5}
          />
        </div>
      </div>

      {/* Footer Note */}
      <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 text-center'>
        <p className='text-xs text-blue-800'>
          <span className='font-medium'>Thank you for helping us improve!</span> Your input helps
          make our AI more accurate.
        </p>
      </div>
    </div>
  );
});

ImproveAnswerModal.displayName = 'ImproveAnswerModal';

export default ImproveAnswerModal;
